/**
 * The small compositions the four capabilities share — kit exports arranged
 * with ProDoc's vocabulary (UI_SYSTEM §8: composition is app-local; a second
 * implementation of a kit control is not). Every status colour comes from
 * `Badge`'s tones.
 */

import { Badge, formatDate } from '@fiveox/ui'
import type { BadgeTone } from '@fiveox/ui'
import { File, FileArchive, FileImage, FileSpreadsheet, FileText, FileType, Mail } from 'lucide-react'

import { READING_WORDS, readingReasonText, type ReadingState } from './extraction-model'
import { DOCX, EML_MEDIA_TYPE, MSG_MEDIA_TYPE, ZIP_MEDIA_TYPE, fileKindOf, type DocumentStatus, type FileKind, type ReviewState } from './prodoc-client'

const KIND_ICON: Record<FileKind, typeof File> = {
  pdf: FileText,
  image: FileImage,
  sheet: FileSpreadsheet,
  text: FileType,
  file: File,
}

/** W31 (FR-68): a mark of their own for the kinds a `FileKind` does not name — an e-mail and an archive. */
const MEDIA_ICON: Record<string, typeof File> = {
  [EML_MEDIA_TYPE]: Mail,
  [MSG_MEDIA_TYPE]: Mail,
  [ZIP_MEDIA_TYPE]: FileArchive,
}

/** The file's kind, as a mark beside its name. */
export function FileMark({ mediaType, size = 'md' }: { mediaType: string | null | undefined; size?: 'sm' | 'md' }) {
  const Icon = (mediaType ? MEDIA_ICON[mediaType] : undefined) ?? KIND_ICON[fileKindOf(mediaType)]
  return (
    <span
      className={`bg-muted text-muted-foreground flex shrink-0 items-center justify-center rounded-lg ${size === 'md' ? 'h-9 w-9' : 'h-7 w-7'}`}
      aria-hidden
    >
      <Icon size={size === 'md' ? 18 : 15} strokeWidth={1.75} />
    </span>
  )
}

/** W6: `expired` is a tombstone — the record and its history kept, the bytes removed (FR-26). */
const STATUS: Record<DocumentStatus, { label: string; tone: BadgeTone }> = {
  stored: { label: 'Stored', tone: 'success' },
  archived: { label: 'Archived', tone: 'neutral' },
  expired: { label: 'Expired', tone: 'error' },
}

export function statusLabel(status: string): string {
  return STATUS[status as DocumentStatus]?.label ?? status.replace(/_/g, ' ')
}

export function StatusBadge({ status, size = 'md' }: { status: string; size?: 'sm' | 'md' }) {
  const def = STATUS[status as DocumentStatus]
  return (
    <Badge tone={def?.tone ?? 'neutral'} size={size}>
      {statusLabel(status)}
    </Badge>
  )
}

/**
 * The four-eyes state (FR-20) — its own badge beside storage's, never folded
 * into it (D1): an archived document keeps the review state it had.
 */
const REVIEW: Record<ReviewState, { label: string; tone: BadgeTone }> = {
  uploaded: { label: 'Not reviewed', tone: 'neutral' },
  needs_review: { label: 'Needs review', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'error' },
}

export function reviewLabel(state: string | null | undefined): string {
  if (!state) return REVIEW.uploaded.label
  return REVIEW[state as ReviewState]?.label ?? state.replace(/_/g, ' ')
}

/** A review state as a badge. `uploaded` — nothing asked of a reviewer — shows only when `always` says so. */
export function ReviewBadge({ state, size = 'md', always = false }: { state: string | null | undefined; size?: 'sm' | 'md'; always?: boolean }) {
  const current = (state ?? 'uploaded') as ReviewState
  if (current === 'uploaded' && !always) return null
  return (
    <Badge tone={REVIEW[current]?.tone ?? 'neutral'} size={size}>
      {reviewLabel(current)}
    </Badge>
  )
}

/** W22 (FR-55): the five words' tones — a failure is the only red. */
const READING_TONE: Record<ReadingState, BadgeTone> = {
  none: 'neutral',
  reading: 'info',
  needs_review: 'warning',
  accepted: 'success',
  failed: 'error',
}

/**
 * Where a document's reading stands, in FR-55's five words (*Stored*,
 * *Reading*, *Needs review*, *Accepted*, *Failed*) — the service's
 * `reading.state`, never a guess; its reason in the title. Nothing for a
 * document whose answer carried no reading.
 */
export function ReadingBadge({ reading, size = 'md' }: { reading: { state: ReadingState; reason?: string | null } | null | undefined; size?: 'sm' | 'md' }) {
  if (!reading) return null
  const why = readingReasonText(reading.reason)
  return (
    <span title={why || undefined} data-reading={reading.state}>
      <Badge tone={READING_TONE[reading.state]} size={size}>
        {READING_WORDS[reading.state]}
      </Badge>
    </span>
  )
}

/** A media type as a reader says it: "PDF", "PNG image", "Spreadsheet", "CSV", "Word document", "JSON", "Text file", "E-mail", "Outlook message", "ZIP archive". */
export function mediaLabel(mediaType: string | null | undefined): string {
  if (!mediaType) return 'File'
  if (mediaType === 'application/pdf') return 'PDF'
  if (mediaType === 'text/csv') return 'CSV'
  if (mediaType === 'application/json') return 'JSON'
  if (mediaType === 'text/plain') return 'Text file'
  if (mediaType === EML_MEDIA_TYPE) return 'E-mail'
  if (mediaType === MSG_MEDIA_TYPE) return 'Outlook message'
  if (mediaType === ZIP_MEDIA_TYPE) return 'ZIP archive'
  if (mediaType === DOCX) return 'Word document'
  if (mediaType.startsWith('image/')) return `${mediaType.slice(6).toUpperCase()} image`
  if (mediaType.includes('spreadsheet') || mediaType.includes('ms-excel')) return 'Spreadsheet'
  return mediaType
}

/** "2 days ago" for a recent instant, the date for anything older. */
export function when(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.round(diff / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
  return formatDate(iso)
}

/** "1 document", "3 documents". */
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
